import React from 'react';
import { LockIcon } from 'lucide-react';
import { ApplicationForm } from '../components/ApplicationForm';
import { Footer } from '../components/Footer';
import { SurveyEmbed } from '../components/SurveyEmbed';
import { ChevronDown } from 'lucide-react';
const STONE_IMAGE = 'https://cdn.magicpatterns.com/uploads/dNGzGmH9ePzfLNEdm6b28J/octi_layered_geometric_sigils_embedded_into_dark_stone-like_t_396a04d3-b0ef-47dd-a400-403a34c1bf9b_3.png';
export function MeetingPage() {
  return <main className="min-h-screen bg-[#0a0a0a] text-[#e0e0e0] relative overflow-hidden selection:bg-white/10 selection:text-white pb-24" data-id="element-22">
      {/* Stone image as full background — low opacity */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-[0.12]" style={{
      backgroundImage: `url(${STONE_IMAGE})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }} data-id="element-23"></div>

      {/* Noise texture overlay */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.035]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'repeat',
      backgroundSize: '200px 200px'
    }} data-id="element-24"></div>

      {/* Pulsating ambient orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden" data-id="element-25">
        {/* Primary orb — upper center, cyan */}
        <div className="absolute rounded-full" style={{
        width: '800px',
        height: '800px',
        top: '25%',
        left: '50%',
        background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)',
        animation: 'bg-pulse-1 8s ease-in-out infinite',
        filter: 'blur(100px)'
      }} data-id="element-26"></div>

        {/* Secondary orb — lower right, cyan-white */}
        <div className="absolute rounded-full" style={{
        width: '600px',
        height: '600px',
        top: '70%',
        left: '65%',
        background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)',
        animation: 'bg-pulse-2 11s ease-in-out infinite',
        filter: 'blur(120px)'
      }} data-id="element-27"></div>

        {/* Tertiary orb — upper left, warm white */}
        <div className="absolute rounded-full" style={{
        width: '500px',
        height: '500px',
        top: '10%',
        left: '20%',
        background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)',
        animation: 'bg-pulse-3 14s ease-in-out infinite',
        filter: 'blur(130px)'
      }} data-id="element-28"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32" data-id="element-29">
        {/* Hero Section */}
        <section className="text-center mb-16 md:mb-24" data-id="element-30">
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl uppercase tracking-[0.15em] text-transparent bg-clip-text bg-gradient-to-b from-[#b0f0ff] via-[#b0b0b0] to-[#555555]" data-id="element-31">
            Expert Help
          </h1>

          <div className="mt-10 flex justify-center items-center gap-4" data-id="element-32">
            <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-[#00F0FF]/20" data-id="element-33"></div>
            <div className="w-1.5 h-1.5 rotate-45 border border-[#00F0FF]/30" data-id="element-34"></div>
            <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-[#00F0FF]/20" data-id="element-35"></div>
          </div>
        </section>

        {/* Stone Image with Lock — lock only, no image */}
        <section className="mb-8 md:mb-12" data-id="element-36">
          <div className="relative max-w-3xl mx-auto flex items-center justify-center py-4 md:py-6" data-id="element-37">
            {/* Centered lock with animations */}
            <div className="relative" data-id="element-38">
              {/* Pulsing cyan glow behind lock */}
              <div className="absolute inset-0 rounded-full bg-[#00F0FF]/[0.08] blur-xl" style={{
              animation: 'pulse-glow 5s ease-in-out infinite',
              margin: '-20px'
            }} data-id="element-39"></div>

              {/* Slow-rotating outer ring */}
              <div className="absolute inset-0 rounded-full" style={{
              animation: 'slow-rotate 60s linear infinite',
              margin: '-16px',
              border: '1px dashed rgba(0, 240, 255, 0.18)'
            }} data-id="element-40"></div>

              {/* Second rotating ring — opposite direction, slightly larger */}
              <div className="absolute inset-0 rounded-full" style={{
              animation: 'slow-rotate 45s linear infinite reverse',
              margin: '-28px',
              border: '1px dashed rgba(0, 240, 255, 0.08)'
            }} data-id="element-41"></div>

              {/* Lock container */}
              <div className="relative p-7 md:p-8 rounded-full border border-[#00F0FF]/10 bg-[#0a0a0a]/60 backdrop-blur-lg shadow-[0_0_100px_rgba(10,10,10,0.95)]" data-id="element-42">
                <LockIcon className="w-12 h-12 md:w-16 md:h-16 text-white/70" strokeWidth={1} data-id="element-43" />
              </div>
            </div>
          </div>
        </section>

        {/* Clear Text Section */}
        <section className="max-w-2xl mx-auto text-center mb-16 md:mb-20 relative" data-id="element-44">
          {/* Subtle glow behind the panel */}
          <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
          boxShadow: '0 0 100px rgba(0, 240, 255, 0.04), 0 0 200px rgba(0, 240, 255, 0.02)'
        }} data-id="element-45"></div>

          {/* Panel container */}
          <div className="relative border border-white/[0.06] rounded-2xl bg-white/[0.02] backdrop-blur-sm px-8 md:px-14 py-12 md:py-16 overflow-hidden" data-id="element-46">
            {/* Top accent line */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-[1px] bg-gradient-to-r from-transparent via-[#00F0FF]/20 to-transparent" data-id="element-47"></div>

            {/* Corner accents — top left */}
            <div className="absolute top-4 left-4 w-6 h-6" data-id="element-48">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#00F0FF]/20 to-transparent" data-id="element-49"></div>
              <div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-[#00F0FF]/20 to-transparent" data-id="element-50"></div>
            </div>
            {/* Corner accents — top right */}
            <div className="absolute top-4 right-4 w-6 h-6" data-id="element-51">
              <div className="absolute top-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#00F0FF]/20 to-transparent" data-id="element-52"></div>
              <div className="absolute top-0 right-0 h-full w-[1px] bg-gradient-to-b from-[#00F0FF]/20 to-transparent" data-id="element-53"></div>
            </div>
            {/* Corner accents — bottom left */}
            <div className="absolute bottom-4 left-4 w-6 h-6" data-id="element-54">
              <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#00F0FF]/20 to-transparent" data-id="element-55"></div>
              <div className="absolute bottom-0 left-0 h-full w-[1px] bg-gradient-to-t from-[#00F0FF]/20 to-transparent" data-id="element-56"></div>
            </div>
            {/* Corner accents — bottom right */}
            <div className="absolute bottom-4 right-4 w-6 h-6" data-id="element-57">
              <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#00F0FF]/20 to-transparent" data-id="element-58"></div>
              <div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-[#00F0FF]/20 to-transparent" data-id="element-59"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 space-y-7" data-id="element-60">
              <p className="font-body text-base md:text-lg text-[#888888] leading-relaxed tracking-wide" data-id="element-61">
                This is for people who want to understand{' '}
                <span className="text-white font-semibold" data-id="element-62">EXACTLY</span> what
                to do based on the most up-to-date science &amp; research
                available.
              </p>

              {/* Decorative line before key statement */}

              <p className="font-heading text-lg md:text-xl text-[#c0d8e0] uppercase tracking-[0.1em] leading-relaxed" data-id="element-63">
                But be aware — this is a private service and is not available to
                everyone.
              </p>

              {/* Decorative line after key statement */}
              <div className="flex justify-center items-center gap-3" data-id="element-64">
                <div className="h-[1px] w-10 bg-gradient-to-r from-transparent to-[#00F0FF]/15" data-id="element-65"></div>
                <div className="w-1 h-1 rotate-45 bg-[#00F0FF]/20" data-id="element-66"></div>
                <div className="h-[1px] w-10 bg-gradient-to-l from-transparent to-[#00F0FF]/15" data-id="element-67"></div>
              </div>

              <p className="font-heading text-2xl md:text-3xl lg:text-4xl text-white font-bold uppercase tracking-[0.12em] leading-tight" style={{
              textShadow: '0 0 40px rgba(0, 240, 255, 0.1), 0 0 80px rgba(0, 240, 255, 0.04)'
            }} data-id="element-68">
                Fill out the form below
              </p>

              <p className="font-body text-lg md:text-xl text-white leading-relaxed tracking-wide -mt-4" data-id="element-69">
                to see if you're a good fit to work with our specialists.
              </p>

              {/* Decorative line before next steps */}

              <p className="font-body text-base md:text-lg text-[#888888] leading-relaxed tracking-wide" data-id="element-70">
                We will go through your submission and reveal the next phase.
              </p>

              <p className="font-body text-base md:text-lg text-[#888888] leading-relaxed tracking-wide" data-id="element-71">
                If approved, you will have{' '}
                <span className="text-white font-semibold" data-id="element-72">
                  full, unlocked access
                </span>{' '}
                to the service.
              </p>
            </div>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-[1px] bg-gradient-to-r from-transparent via-[#00F0FF]/15 to-transparent" data-id="element-73"></div>
          </div>
        </section>

        {/* Geometric divider — cyan accents */}
        <div className="flex justify-center items-center gap-3 mb-8 md:mb-10" data-id="element-74">
          <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#00F0FF]/12" data-id="element-75"></div>
          <div className="w-2 h-2 rotate-45 border border-[#00F0FF]/20" data-id="element-76"></div>
          <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#00F0FF]/12" data-id="element-77"></div>
        </div>

        {/* Application Form Section */}
        <section className="relative" data-id="element-78">
          <ApplicationForm data-id="element-79" />
        </section>

        {/* Skip to survey button */}

        {/* Geometric divider */}
        <div className="flex justify-center items-center gap-3 mb-8 md:mb-10" data-id="element-80">
          <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#00F0FF]/12" data-id="element-81"></div>
          <div className="w-2 h-2 rotate-45 border border-[#00F0FF]/20" data-id="element-82"></div>
          <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#00F0FF]/12" data-id="element-83"></div>
        </div>

        {/* Survey Section */}
      </div>

      <Footer data-id="element-84" />
    </main>;
}